import React, { useEffect, useState, useRef, memo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useApolloClient, ApolloQueryResult } from '@apollo/client';
import cx from 'classnames';

import { leftNavConstants, LoadingSpinner, PreFilters } from '@trqk/components';
import {
    locale,
    getWebpackEnvVar,
    mapPreFilterConfigsToParamInputs,
    mapSearchParamsToFilters,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { useAutoSize, usePreFilteredList } from '@trqk/hooks';

import {
    useTokenModal,
    useEntities,
    useToast,
    usePaymentPlanStatus,
} from '../../../../../hooks';
import { PerformancesEmptyState, PerformancesTable } from './components';
import { mapRawPerformanceToPerformanceTableItem } from './helpers';
import { preFilterConfigs, minWidthOffset } from './constants';

const { AUTO_SIZE_OFFSETS, HEIGHTS, WIDTHS } = leftNavConstants;
const { GET_STATEMENT_PERFORMANCE_DATA } = queryStrings;

/**
 * @typedef {PerformanceDataProps}
 * @property {string} selectedIdsString a JSON.stringified array of selected statement IDs, used to
 * determine when to fetch new RawPerformance data by checking equality with useEffect.
 */
interface PerformanceDataProps {
    selectedIdsString: string;
}

/**
 * A wrapper for the PerformancesTable and PreFilters. Has several responsibilities:
 * 1. Fetches an array of RawPerformance data for the currently selected statements and map it to an
 * array of PerformanceTableItem objects
 * 2. Use that array of PerformanceTableItem objects to initialize a preFilter state machine with
 * its 'SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS' event
 * 3. Pass the current state of the `filteredItems` list from the preFilter state machine to the
 * PerformancesTable component. This is how the PreFilters component is able to set the
 * state of the data that is rendered by react-table.
 * @param {PerformanceDataProps} props
 * @returns JSX.Element
 */
export const PerformanceData = memo(
    ({ selectedIdsString }: PerformanceDataProps): JSX.Element | null => {
        const client = useApolloClient();
        const location = useLocation();
        const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
        const { selectedEntity, setStateListeners } = useEntities();
        const { add: addToast } = useToast();
        const { getToken } = useTokenModal();
        const entityRoleType = selectedEntity?.role_type ?? null;
        const [loadingPreFilters, setLoadingPreFilters] = useState(true);
        const [reloadingData, setReloadingData] = useState(false);

        const fetchAllItems = useCallback(
            (
                dependencies: [ProRoleType | null, string, string],
            ): Promise<PerformanceTableItem[]> => {
                setReloadingData(true);

                const roleTypeDep = dependencies[0];
                const selectedIdsStringDep = dependencies[1];
                const searchParams = new URLSearchParams(dependencies[2]);
                const paramInputs =
                    mapPreFilterConfigsToParamInputs(preFilterConfigs);
                const filterState = mapSearchParamsToFilters(
                    searchParams,
                    paramInputs,
                );

                const workTitleFilterValue = filterState.reduce(
                    (acc: Maybe<string | number>, filter) => {
                        if (acc !== null) {
                            return acc;
                        }

                        if (filter.id === 'workTitle') {
                            return filter.paramValue;
                        }

                        return acc;
                    },
                    null,
                );

                const statementIds: string[] = JSON.parse(selectedIdsStringDep);

                const performanceDataInputs = statementIds.map(statementId => ({
                    statementId,
                    workTitle: workTitleFilterValue ?? undefined,
                }));

                return new Promise((resolve, reject) => {
                    getToken((token: string) => {
                        client
                            .query<{
                                getStatementPerformanceData: {
                                    performances: RawPerformance[];
                                    errors: GetStatementPerformanceDataError[];
                                };
                            }>({
                                query: GET_STATEMENT_PERFORMANCE_DATA,
                                fetchPolicy: 'network-only',
                                variables: {
                                    token,
                                    inputs: performanceDataInputs,
                                    trqkDirectoryUrl,
                                },
                            })
                            .then(
                                (
                                    result: ApolloQueryResult<{
                                        getStatementPerformanceData: {
                                            performances: RawPerformance[];
                                            errors: GetStatementPerformanceDataError[];
                                        };
                                    }>,
                                ): void => {
                                    const rawPerformances =
                                        result.data?.getStatementPerformanceData
                                            ?.performances ?? null;

                                    const errors =
                                        result.data?.getStatementPerformanceData
                                            ?.errors ?? [];

                                    const performanceErrorIds = errors.map(
                                        ({
                                            statementId,
                                        }: GetStatementPerformanceDataError): string =>
                                            statementId,
                                    );

                                    if (performanceErrorIds.length > 0) {
                                        const content = `${locale.enUS.STATEMENTS.PERFORMANCES.ERRORS.FETCH_PERFORMANCE_DATA} ${performanceErrorIds}`;

                                        addToast({
                                            kind: 'warning-banner',
                                            content,
                                        });
                                    }

                                    if (
                                        rawPerformances !== null &&
                                        roleTypeDep !== null
                                    ) {
                                        const allItems = rawPerformances.map(
                                            (rawPerformance: RawPerformance) =>
                                                mapRawPerformanceToPerformanceTableItem(
                                                    rawPerformance,
                                                    roleTypeDep,
                                                ),
                                        );

                                        resolve(allItems);
                                    }
                                },
                            )
                            .catch((error): void => {
                                reject(error);
                            });
                    });
                });
            },
            [trqkDirectoryUrl],
        );

        const tableElementRef = useRef<HTMLDivElement>(null);
        const { showFreeTrialFooter } = usePaymentPlanStatus();
        const { height, getSize, width } = useAutoSize({
            elementRef: tableElementRef,
            resizeRate: 400,
            rightOffset: AUTO_SIZE_OFFSETS.RIGHT,
            bottomOffset: showFreeTrialFooter
                ? HEIGHTS.ACCOUNT_DROPDOWN + AUTO_SIZE_OFFSETS.BOTTOM
                : AUTO_SIZE_OFFSETS.BOTTOM,
            minWidth: WIDTHS.MAIN_XL - minWidthOffset,
        });

        const { current, send } = usePreFilteredList<PerformanceTableItem>({
            fetchAllItems,
            fetchAllItemsDeps: [
                entityRoleType,
                selectedIdsString,
                location.search,
            ],
            onFetchAllItemsSuccess: () => {
                // It takes some time to paint the filters and table on the screen once the data is
                // ready within the preFilter state machine. This gives the browser some time to do
                // that work before we un-hide the table after the 2nd or more fetches.
                setTimeout(() => {
                    setReloadingData(false);
                }, 500);

                setTimeout(getSize, 1000);
                setTimeout(getSize, 2000);
            },
            preFilterConfigs,
        });

        useEffect(() => {
            // It takes some time to paint the filters and table on the screen once the data is
            // ready within the preFilter state machine. This gives the browser some time to do that
            // work before we un-hide the table and filters after the first fetch.
            if (current.value === 'ready') {
                setTimeout(() => {
                    setLoadingPreFilters(false);
                }, 500);
                setTimeout(getSize, 1000);
                setTimeout(getSize, 2000);
            }
        }, [current.value]);

        useEffect(() => {
            if (tableElementRef.current) {
                getSize();
            }
        }, [tableElementRef.current]);

        let tableEmptyState = null;

        if (!loadingPreFilters && current.context.filteredItems.length < 1) {
            tableEmptyState = (
                <PerformancesEmptyState height={height} width={width} />
            );
        } else if (reloadingData) {
            tableEmptyState = (
                <div
                    className="flex items-center justify-center w-full h-full mt-32"
                    style={{ height: 300 }}
                >
                    <LoadingSpinner />
                </div>
            );
        }

        return (
            <>
                {loadingPreFilters && (
                    <div
                        className="flex items-center justify-center w-full h-full mt-32"
                        style={{ width: WIDTHS.MAIN_XL, height: 500 }}
                    >
                        <LoadingSpinner />
                    </div>
                )}
                <div
                    className={cx(
                        'mt-6 flex flex-col w-full h-full rounded-lg bg-white',
                        { hidden: loadingPreFilters },
                    )}
                >
                    {current.value === 'ready' && (
                        <PerformancesTable
                            emptyState={tableEmptyState}
                            height={height}
                            ref={tableElementRef}
                            items={current.context.filteredItems}
                            refreshTime={current.context.refreshTime}
                            filters={
                                <PreFilters<PerformanceTableItem>
                                    current={current}
                                    getSize={getSize}
                                    preFilterConfigs={preFilterConfigs}
                                    setStateListeners={setStateListeners}
                                    send={send}
                                    tableName="performanceData"
                                    width={width}
                                />
                            }
                            width={width}
                        />
                    )}
                </div>
            </>
        );
    },
);
